import { useEffect, useRef, useState } from 'react'
import { useSetState } from 'ahooks'
import { Button, DatePicker, Form, InputNumber, message, TreeSelect, Upload } from 'antd'
import {
  SActionBtn,
  SBack,
  SDrawerOrModal,
  SFuncModal,
  SFuncModalFormRef,
  SLocalTreeSelected,
  SLowcodeList,
  SMessage,
  STableActions,
} from 'shj-components'
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons'

import { httpOrgList } from '@/services/homeService.ts'
import {
  getPriceImportFeesPage,
  getPriceItemSpecialConfigPage,
  getProductCommonPage,
  getProductManagementPage,
  getProjectPage,
  priceImportFeesBatchDelete,
  priceImportFeesBatchUpdateDate,
  priceImportFeesImport,
  priceItemSpecialConfigBatchDelete,
  priceItemSpecialConfigDelete,
  priceItemSpecialConfigImport,
} from '@/services/product.ts'
import { config } from './config.tsx'
import AddOrEditConfigure from './addOrEdit'
import { apiData2Table } from '@/utils/data.ts'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { debounce, get } from 'lodash-es'
import { fileDownload } from '@/utils/download.ts'
import UrlConfig from '@/api/UrlConfigs.ts'
import DebounceSearchSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import dayjs from 'dayjs'

const ImportConfigList = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [search] = useSearchParams()
  const name = search.get('name')
  const type = search.get('type')
  const listRef = useRef()
  const editRef = useRef<SFuncModalFormRef>({ onFinish: undefined })
  const [state, setState] = useSetState<any>({
    exportLoading: false,
    loading: false,
    visible: false,
    record: {},
  })
  const [minPrice,setMinPrice] = useState(0)
  const [form] = Form.useForm()

  const getOptions = async () => {
    const companyList = await httpOrgList()
    return Promise.resolve({ companyList: [{ id: '0', name: '通用' }, ...companyList] })
  }

  const fetchList = ({ current, pageSize, query }) => {
    if (!id) return
    return new Promise((resolve, reject) => {
      getPriceImportFeesPage({
        params: { pageId: current, pageSize },
        data: { ...query, priceItemId: id, searchFlag: true },
      })
        .then(res => {
          resolve(apiData2Table(res))
        })
        .catch(reject)
    })
  }

  const onAddOrEdit = (record = {}) => {
    setState({ visible: true, record: { ...record } })
  }

  const delConfirm = apiFun => {
    SMessage.confirm({
      onOk: () => {
        return apiFun().then((res: any) => {
          message.success(res.message || '操作成功')
          listRef.current?.reload()
        })
      },
    })
  }

  const onDel = (id: string) => {
    delConfirm(() => priceImportFeesBatchDelete([id]))
  }

  const onBatchDel = ({ selectedRowKeys }: { selectedRowKeys: string[] }) => {
    delConfirm(() => priceImportFeesBatchDelete(selectedRowKeys))
  }

  const onSubmit = () => {
    editRef.current.onFinish?.()
  }

  const goBack = () => {
    navigate(`/config/priceTagManage?type=${type}`, { state: { saveCache: true } })
  }

  const onCancel = () => {
    setState({ visible: false, record: {} })
  }

  const onImport = (upload: any) => {
    const { file, onSuccess, onError } = upload
    const data = new FormData()
    data.append('file', file)
    data.append('priceItemId', id)
    return priceImportFeesImport(data)
      .then((res: any) => {
        const msg = get(res, 'data') || ''
        listRef.current && listRef.current.reload()
        onSuccess && onSuccess()
        message.success(msg || '导入成功')
      })
      .catch(onError)
  }

  const onExport = () => {
    setState({ exportLoading: true })
    fileDownload(UrlConfig.PlusProductWeb.PriceImportFeesDownload)
      .then(() => setState({ exportLoading: false }))
      .catch(() => setState({ exportLoading: false }))
  }

  const onBatchUpDateStart = ({ selectedRowKeys }: { selectedRowKeys: string[] }) => {
    postData({
      ids: selectedRowKeys,
      type: 'startDate',
      label: '开始日期',
      required: true,
    })
  }
  const onBatchUpDateEnd = ({ selectedRowKeys }: { selectedRowKeys: string[] }) => {
    postData({
      ids: selectedRowKeys,
      type: 'endDate',
      label: '结束日期',
      required: false,
    })
  }
  const postData = data => {
    const dialog = SFuncModal({
      title: '修改日期',
      content: (
        <Form form={form}>
          <Form.Item
            label={data.label}
            name="date"
            required={data.required}
            rules={[{ required: data.required, message: data.label + '不能为空' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      ),
      onOk: () => {
        form.validateFields().then((values: Record<string, string>) => {
          values.date = values.date && dayjs(values.date).format('YYYY-MM-DD')
          priceImportFeesBatchUpdateDate({
            data: {
              ...values,
              ids: data.ids,
              type: data.type,
            },
          })
            .then(res => {
              message.success('操作成功')
              form.resetFields()
              listRef.current?.reload()
              dialog.destroy()
            })
            .catch(() => {
              form.resetFields()
              dialog.destroy()
            })
        })
      },
      onCancel: () => {
        form.resetFields()
        dialog.destroy()
      },
    })
  }

  const extensions = {
    request: { fetchList, getOptions },
    events: { onBatchDel, onBatchUpDateStart, onBatchUpDateEnd },
    customRender: {
      jsonRender(v: any) {
        const { shops: list } = v && JSON.parse(v)
        return list?.map(item => item.name)?.join(', ')
      },
      actionRender(_: null, record: Record<string, any>) {
        return (
          <STableActions>
            <SActionBtn onClick={() => onAddOrEdit(record)}>编辑</SActionBtn>
            <SActionBtn danger onClick={() => onDel(record.id)}>
              删除
            </SActionBtn>
          </STableActions>
        )
      },
      importRender: () => {
        return (
          <Upload listType="text" customRequest={onImport} itemRender={() => null}>
            <Button type={'primary'} icon={<UploadOutlined />}>
              批量导入
            </Button>
          </Upload>
        )
      },
      exportRender: () => {
        return (
          <Button icon={<DownloadOutlined />} onClick={onExport} loading={state.exportLoading}>
            下载导入模板
          </Button>
        )
      },
      nameRender() {
        return name
      },
      sortRender(v, r, i) {
        return i + 1
      },
      projectListRender: ({ name, form }) => {
        return (
          <DebounceSearchSelect
            allowClear
            label={'产品'}
            fetchOptions={getProductManagementPage}
            onChange={v => form.setFieldsValue({ [name]: v })}
            fieldNames={{ label: 'prodName' }}
          />
        )
      },
      searchSelectRender({ name, form }) {
        return (
          <DebounceSearchSelect
            allowClear
            label={'产品'}
            isCommon={true}
            fetchOptions={getProductCommonPage}
            onChange={v => form.setFieldsValue({ [name]: v })}
            fieldNames={{ label: 'prodName' }}
            style={{ width: '100%' }}
            fixedParams={{
              prodTypes: ['PRODUCT_PACKAGE'],
              enabled:null
            }}
          />
        )
      },
      areaRangeRender: ({ name, form }) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InputNumber
              style={{ width: '50%' }}
              placeholder="请输入"
              onChange={v => {
                setMinPrice(v)
                form.setFieldsValue({
                  ['budgetAreaStart']: v,
                })
              }}
            />
            <span> ~ </span>
            <InputNumber
              style={{ width: '50%' }}
              placeholder="请输入"
              min={minPrice}
              onChange={v => {
                form.setFieldsValue({
                  ['budgetAreaEnd']: v,
                })
              }}
            />
          </div>
        )
      },
    },
  }

  return (
    <SBack title={`${name}导入费用`} level={2} goPath={goBack}>
      <div>
        {/*<div style={{ padding: '20px 20px 0 20px', background: '#fff' }}></div>*/}
        <SLowcodeList ref={listRef} searchMode={'group'} config={config} {...extensions} />
        <SDrawerOrModal
          mode={'modal'}
          onCancel={onCancel}
          onOk={onSubmit}
          width={710}
          loading={state.loading}
          open={state.visible}
          title={(state.record.id ? '编辑' : '新增') + '配置'}
        >
          <AddOrEditConfigure
            fn={listRef.current?.reload}
            onClose={onCancel}
            editRef={editRef}
            mode={'modal'}
            setLoading={(loading: boolean) => setState({ loading })}
            priceItemId={id}
            companyList={state.companyList}
            record={state.record}
          />
        </SDrawerOrModal>
      </div>
    </SBack>
  )
}

export default ImportConfigList
